import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { FormQuestionRowOptionsDialog } from './FormQuestionRowOptionsDialog';
import {
  emptyFormQuestionDraftRow,
  FORM_QUESTION_ANSWER_TYPES,
  formQuestionGroupHasPersistedRow,
  formQuestionRowIsPersisted,
  type FormQuestionDraftRow,
  type FormQuestionGroupDraft,
} from './formQuestionGroups';

function jenisPengukuranOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const nama = String(row.nama_jenis ?? '').trim();
  const kode = String(row.kode_jenis ?? '').trim();
  return [nama, kode].filter(Boolean).join(' — ') || String(row.documentId ?? '');
}

function nextSortOrder(rows: Array<{ sortOrder: string }>): number {
  let max = -1;
  for (const row of rows) {
    const n = Number(row.sortOrder);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

type Props = {
  draft: FormQuestionGroupDraft;
  onChange: (next: FormQuestionGroupDraft) => void;
  jenisOptions: DynamicEntityRow[];
  jenisLoading?: boolean;
};

export function FormQuestionGroupEditor({ draft, onChange, jenisOptions, jenisLoading = false }: Props) {
  const selectedJenis = jenisOptions.filter((opt) => draft.jenisPengukuranDocumentIds.includes(opt.documentId));
  const showOptionsLinkColumn = formQuestionGroupHasPersistedRow(draft);
  const [optionsRow, setOptionsRow] = useState<FormQuestionDraftRow | null>(null);

  function patchQuestions(next: FormQuestionDraftRow[]) {
    onChange({ ...draft, questions: next });
  }

  function patchRow(key: string, patch: Partial<FormQuestionDraftRow>) {
    patchQuestions(draft.questions.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  return (
    <Stack spacing={2} data-testid="form-question-group-editor">
      <TextField
        label="Nama form"
        required
        size="small"
        fullWidth
        value={draft.formName}
        onChange={(ev) => onChange({ ...draft, formName: ev.target.value })}
        helperText="Satu nama form dipakai bersama semua baris pertanyaan di bawah."
        inputProps={{ 'data-testid': 'form-question-group-name' }}
      />
      <Autocomplete
        multiple
        size="small"
        options={jenisOptions}
        value={selectedJenis}
        loading={jenisLoading}
        onChange={(_, rows) =>
          onChange({ ...draft, jenisPengukuranDocumentIds: rows.map((r) => r.documentId) })
        }
        getOptionLabel={(opt) => jenisPengukuranOptionLabel(opt)}
        isOptionEqualToValue={(a, b) => a.documentId === b.documentId}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Jenis pengukuran"
            helperText="Dipakai bersama seluruh pertanyaan form ini. Relasi tetap disimpan per pertanyaan."
            inputProps={{
              ...params.inputProps,
              'data-testid': 'form-question-group-jenis',
            }}
          />
        )}
      />
      <Box>
        <Typography variant="subtitle2">Pertanyaan</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Nilai default terisi otomatis di selesai sampling. Petugas cukup mengubah nilai yang berbeda. Untuk
          boolean isi true atau false. Untuk pilihan tunggal, isi judul opsi yang ingin terpilih.
        </Typography>
        <Box sx={{ overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {showOptionsLinkColumn ? <TableCell sx={{ width: 72 }}>Opsi</TableCell> : null}
                <TableCell sx={{ minWidth: 220 }}>Judul</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Tipe jawaban</TableCell>
                <TableCell sx={{ minWidth: 160 }}>Nilai default</TableCell>
                <TableCell sx={{ width: 88 }}>Urutan</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {draft.questions.map((row, index) => (
                <TableRow key={row.key} data-testid={`form-question-group-row-${index}`}>
                  {showOptionsLinkColumn ? (
                    <TableCell>
                      {formQuestionRowIsPersisted(row) ? (
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => setOptionsRow(row)}
                          data-testid={`form-question-row-options-link-${index}`}
                        >
                          Opsi
                        </Link>
                      ) : null}
                    </TableCell>
                  ) : null}
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      required
                      placeholder="Judul pertanyaan"
                      value={row.title}
                      onChange={(ev) => patchRow(row.key, { title: ev.target.value })}
                      inputProps={{ 'data-testid': `form-question-row-title-${index}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      value={row.answerType}
                      onChange={(ev) => patchRow(row.key, { answerType: ev.target.value })}
                      inputProps={{ 'data-testid': `form-question-row-answer-type-${index}` }}
                    >
                      {FORM_QUESTION_ANSWER_TYPES.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Nilai awal"
                      value={row.defaultValue ?? ''}
                      onChange={(ev) => patchRow(row.key, { defaultValue: ev.target.value })}
                      inputProps={{ 'data-testid': `form-question-row-default-${index}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={row.sortOrder}
                      onChange={(ev) => patchRow(row.key, { sortOrder: ev.target.value })}
                      inputProps={{ 'data-testid': `form-question-row-sort-${index}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Hapus baris">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Hapus baris ${index + 1}`}
                          disabled={draft.questions.length <= 1}
                          onClick={() => patchQuestions(draft.questions.filter((r) => r.key !== row.key))}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Button
          type="button"
          size="small"
          startIcon={<AddIcon />}
          sx={{ mt: 1 }}
          onClick={() => patchQuestions([...draft.questions, emptyFormQuestionDraftRow(nextSortOrder(draft.questions))])}
          data-testid="form-question-group-add-row"
        >
          Tambah pertanyaan
        </Button>
      </Box>
      <FormQuestionRowOptionsDialog
        open={Boolean(optionsRow?.documentId)}
        questionDocumentId={optionsRow?.documentId ?? ''}
        questionTitle={optionsRow?.title ?? ''}
        onClose={() => setOptionsRow(null)}
      />
    </Stack>
  );
}
